import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client';
import { formatMkd, formatDate } from '../lib/format';

/**
 * One staff member's payment history, grouped by month with a paid/partial
 * status. Ports frmStaffDetails.
 */

interface HistoryRow {
  id: number;
  date: string;
  rbaz: number;
  rbonus: number;
  rzbritje: number;
  ravans: number;
  paid: number;
}

interface HistoryMonth {
  year: number;
  month: number;
  monthName: string;
  rows: HistoryRow[];
  paid: number;
  due: number;
  remaining: number;
  status: 'Paguar' | 'Pjesërisht';
}

interface Response {
  history: HistoryMonth[];
  outstanding: number;
  /** Owed for last month alone. null when the staff member has no start date. */
  lastMonthRemaining: number | null;
  /** Whole months since the start date. null when there is no start date. */
  monthsOfService: number | null;
  totalPaid: number;
}

export default function StaffDetailsModal({
  staffId,
  fullName,
  onClose,
}: {
  staffId: number;
  fullName: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['staff-detail', staffId],
    queryFn: () => apiGet<Response>(`/stafi/${staffId}`),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const history = detailQuery.data?.history ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Rrogat — {fullName}</h2>
            <p className="text-sm text-muted">Historiku i pagesave sipas muajit</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Mbyll
          </button>
        </div>

        {/*
          The four figures frmStaffDetails shows above the grid: total paid,
          total outstanding (SetLeftSalaryLabel:283), what is still owed for last
          month alone (SetLeftSalaryLastMonthLabel:388), and how long they have
          worked here (SetMemberMonthsLabel:358).
        */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-xs font-medium text-muted">Totali i paguar</div>
            <div className="tnum mt-1 text-xl font-semibold">
              {formatMkd(detailQuery.data?.totalPaid ?? 0)}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-xs font-medium text-muted">Mbetur për t’u paguar</div>
            <div
              className={`tnum mt-1 text-xl font-semibold ${
                (detailQuery.data?.outstanding ?? 0) > 0 ? 'text-red-600' : 'text-emerald-700'
              }`}
            >
              {formatMkd(detailQuery.data?.outstanding ?? 0)}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-xs font-medium text-muted">Mbetur muajin e kaluar</div>
            <div
              className={`tnum mt-1 text-xl font-semibold ${
                (detailQuery.data?.lastMonthRemaining ?? 0) > 0
                  ? 'text-red-600'
                  : 'text-emerald-700'
              }`}
            >
              {detailQuery.data?.lastMonthRemaining == null
                ? '—'
                : formatMkd(detailQuery.data.lastMonthRemaining)}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-xs font-medium text-muted">Anëtar prej</div>
            <div className="tnum mt-1 text-xl font-semibold">
              {detailQuery.data?.monthsOfService == null
                ? '—'
                : `${detailQuery.data.monthsOfService} muaj`}
            </div>
          </div>
        </div>

        {detailQuery.isLoading && (
          <div className="py-10 text-center text-muted">Duke ngarkuar…</div>
        )}

        {!detailQuery.isLoading && history.length === 0 && (
          <div className="py-10 text-center text-muted">Nuk ka pagesa të regjistruara.</div>
        )}

        <div className="space-y-4">
          {history.map((m) => (
            <div key={`${m.year}-${m.month}`} className="overflow-hidden rounded-xl border border-slate-200">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                <span className="font-medium">
                  {m.monthName} {m.year}
                </span>
                <span className="flex items-center gap-3 text-sm">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      m.status === 'Paguar'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {m.status}
                  </span>
                  <span className="tnum text-muted">
                    {formatMkd(m.paid)} / {formatMkd(m.due)}
                  </span>
                  {m.remaining > 0 && (
                    <span className="tnum text-red-600">mbetur {formatMkd(m.remaining)}</span>
                  )}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-muted">
                      <th className="px-3 py-2 font-semibold">Data</th>
                      <th className="px-3 py-2 text-right font-semibold">Baza</th>
                      <th className="px-3 py-2 text-right font-semibold">Bonusi</th>
                      <th className="px-3 py-2 text-right font-semibold">Zbritja</th>
                      <th className="px-3 py-2 text-right font-semibold">Avansi</th>
                      <th className="px-3 py-2 text-right font-semibold">Totali</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.rows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-50 last:border-0">
                        <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="tnum px-3 py-1.5 text-right">{formatMkd(r.rbaz)}</td>
                        <td className="tnum px-3 py-1.5 text-right text-emerald-700">
                          +{formatMkd(r.rbonus)}
                        </td>
                        <td className="tnum px-3 py-1.5 text-right text-red-600">
                          −{formatMkd(r.rzbritje)}
                        </td>
                        <td className="tnum px-3 py-1.5 text-right">{formatMkd(r.ravans)}</td>
                        <td className="tnum px-3 py-1.5 text-right font-medium">
                          {formatMkd(r.paid)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
